import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../lib/api';
import { getAnonId } from '../lib/tracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [phase, setPhase] = useState('auth'); // 'auth' | 'checkout'
  const [isReturningSubscriber, setIsReturningSubscriber] = useState(false);

  const handleRetry = () => {
    // Reset state and try again
    setError(null);
    setErrorDetails(null);
    setRetrying(true);
    hasProcessed.current = false;
    
    // Re-trigger auth by reloading the page
    window.location.reload();
  };

  const handleGoHome = () => {
    // Clear the hash and go home
    window.location.href = '/';
  };

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async (currentRetry = 0) => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        console.debug('[Auth] Processing session, retry:', currentRetry);
        console.debug('[Auth] Backend URL:', BACKEND_URL);

        if (!sessionId) {
          throw new Error('No se encontró el ID de sesión en la URL');
        }

        // Call backend to process session with timeout
        console.debug('[Auth] Calling backend at:', BACKEND_URL);
        const response = await api.post(
          `/auth/session`,
          { session_id: sessionId, anon_id: getAnonId() },
          { 
            withCredentials: true,
            timeout: 20000 // 20 second timeout for mobile
          }
        );

        console.debug('[Auth] Backend response:', response.status);
        const { user, session_token } = response.data;

        // Store session token in localStorage as fallback for mobile browsers
        // that may block third-party cookies
        if (session_token) {
          try {
            localStorage.setItem('wtpl_session_token', session_token);
            console.debug('[Auth] Session token stored in localStorage');
          } catch (e) {
            console.warn('[Auth] Could not store session token in localStorage:', e);
          }
        }

        // Check if there was an intent stored before login
        const intent = localStorage.getItem('wtpl_intent');
        if (intent === 'subscribe') {
          localStorage.removeItem('wtpl_intent');

          // Track whether this user already used their trial so the
          // transition message doesn't lie about "7 días gratis"
          if (user?.has_previous_subscription) {
            setIsReturningSubscriber(true);
          }

          // Check if user already has active subscription - skip checkout if so
          if (user.subscription_status === 'active') {
            console.debug('[Auth] User already has active subscription, skipping checkout');
            navigate('/dashboard', { state: { user }, replace: true });
            return;
          }
          
          // Redirect to checkout only if not subscribed (1 automatic retry on failure)
          setPhase('checkout');
          let checkoutUrl = null;
          for (let attempt = 0; attempt < 2 && !checkoutUrl; attempt++) {
            try {
              const checkoutResponse = await api.post('/subscriptions/create-checkout', {}, { 
                withCredentials: true,
                timeout: 30000
              });
              checkoutUrl = checkoutResponse.data?.url || null;
            } catch (checkoutError) {
              console.error(`[Auth] Checkout error (attempt ${attempt + 1}):`, checkoutError);
              if (attempt === 0) await new Promise((r) => setTimeout(r, 1500));
            }
          }
          if (checkoutUrl) {
            window.location.href = checkoutUrl;
            return;
          }
          // Both attempts failed — continue to dashboard (subscribe CTA lives there)
        } else if (intent === 'starter_pack' || intent === 'full_pack') {
          localStorage.removeItem('wtpl_intent');
          // Navigate to dashboard where they can complete the purchase
          navigate('/dashboard', { state: { user, showPurchase: intent }, replace: true });
          return;
        }

        // Navigate to dashboard with user data
        navigate('/dashboard', { state: { user }, replace: true });
      } catch (err) {
        console.error('[Auth] Error:', err);
        console.error('[Auth] Error code:', err.code);
        console.error('[Auth] Error response:', err.response?.data);
        
        // Retry on network errors (up to 3 retries with increasing delay)
        const isNetworkError = err.code === 'ERR_NETWORK' || 
                               err.code === 'ECONNABORTED' ||
                               err.message === 'Network Error' ||
                               err.message?.includes('timeout');
        
        if (isNetworkError && currentRetry < 3) {
          setRetrying(true);
          setRetryCount(currentRetry + 1);
          const delay = (currentRetry + 1) * 2000; // 2s, 4s, 6s
          console.debug(`[Auth] Retrying in ${delay}ms...`);
          setTimeout(() => {
            hasProcessed.current = false;
            processSession(currentRetry + 1);
          }, delay);
          return;
        }
        
        // Determine error message
        let errorMsg = 'Error desconocido durante la autenticación';
        let details = null;
        
        if (isNetworkError) {
          errorMsg = 'Error de conexión con el servidor';
          details = 'Por favor verifica tu conexión a internet y vuelve a intentar. Si el problema persiste, intenta desde otro navegador o dispositivo.';
        } else if (err.response?.status === 401) {
          errorMsg = 'Sesión inválida o expirada';
          details = 'Tu sesión de Google ha expirado. Por favor inicia sesión nuevamente.';
        } else if (err.response?.status === 500) {
          errorMsg = 'Error del servidor';
          details = err.response?.data?.detail || 'Hubo un problema procesando tu sesión. Por favor intenta de nuevo.';
        } else if (err.response?.data?.detail) {
          errorMsg = err.response.data.detail;
        } else if (err.message) {
          errorMsg = err.message;
        }
        
        setError(errorMsg);
        setErrorDetails(details);
        setRetrying(false);
      }
    };

    processSession(0);
  }, [location, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-[#00D9FF] text-xl mb-4">❌ Error de Autenticación</div>
          <p className="text-white font-medium mb-2">{error}</p>
          {errorDetails && (
            <p className="text-gray-400 text-sm mb-6">{errorDetails}</p>
          )}
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleRetry}
              className="px-6 py-3 bg-gradient-to-r from-[#00D9FF] to-[#00D9FF] text-white font-semibold rounded-lg hover:opacity-90 transition-all"
            >
              🔄 Reintentar
            </button>
            <button
              onClick={handleGoHome}
              className="px-6 py-3 bg-[#262626] text-white font-semibold rounded-lg hover:bg-[#333] transition-all"
            >
              🏠 Ir al Inicio
            </button>
          </div>
          
          <p className="text-gray-600 text-xs mt-6">
            Si el problema continúa, intenta:
            <br />• Borrar caché y cookies
            <br />• Usar modo incógnito
            <br />• Probar desde otro navegador
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500 mx-auto mb-4"></div>
        <p className="text-gray-400">
          {retrying
            ? `Reintentando conexión... (${retryCount}/3)`
            : phase === 'checkout'
              ? 'Preparando tu pago seguro...'
              : 'Autenticando...'}
        </p>
        {phase === 'checkout' && !retrying && (
          <p className="text-[#00D9FF] text-sm mt-2" data-testid="checkout-transition-msg">
            {isReturningSubscriber
              ? 'Te llevamos a Stripe — reactivación por $4.99'
              : 'Te llevamos a Stripe — 7 días gratis, hoy pagas $0'}
          </p>
        )}
        {retrying && (
          <p className="text-gray-600 text-sm mt-2">Por favor espera un momento...</p>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
